import { detectRunnerBoosterCollisions, detectRunnerObstacleCollisions, detectRunnerSparkCollisions } from '../../domain/runner/runnerCollisionDetect';
import { shouldEnterRunnerModifierZone } from '../../domain/runner/runnerModifierZone';
import type { RunnerLevel, RunnerPlayer } from '../../domain/runner/runnerTypes';
import { applyRunnerBoosterPickupUseCase, applyRunnerPlayerHitUseCase } from './runnerUseCases';
import {
  hasRunnerHitInvulnerability,
  hasRunnerShieldBooster,
} from '../../domain/runner/runnerPlayer';

function consumeObstacles(level: RunnerLevel, ids: Set<string>): RunnerLevel {
  if (ids.size === 0) {
    return level;
  }
  return {
    ...level,
    obstacles: level.obstacles.map((o) => {
      if (!ids.has(o.id)) {
        return o;
      }
      if (o.motion?.kind === 'orbit') {
        return { ...o, touched: true };
      }
      return { ...o, consumed: true };
    }),
  };
}

function consumeSparks(level: RunnerLevel, ids: Set<string>): RunnerLevel {
  if (ids.size === 0) {
    return level;
  }
  return {
    ...level,
    sparks: level.sparks.map((s) => (ids.has(s.id) ? { ...s, consumed: true } : s)),
  };
}

function consumeBoosters(level: RunnerLevel, ids: Set<string>): RunnerLevel {
  if (ids.size === 0) {
    return level;
  }
  return {
    ...level,
    boosters: level.boosters.map((b) =>
      ids.has(b.id) ? { ...b, consumed: true } : b,
    ),
  };
}

export function handleRunnerObstacleCollisions(
  level: RunnerLevel,
  player: RunnerPlayer,
  activeCollisionIds: Set<string>,
): {
  level: RunnerLevel;
  player: RunnerPlayer;
  nextActiveCollisionIds: Set<string>;
} {
  if (hasRunnerHitInvulnerability(player)) {
    return { level, player, nextActiveCollisionIds: activeCollisionIds };
  }

  const hits = detectRunnerObstacleCollisions(level, player);
  const newlyConsumed = hits.filter((o) => !activeCollisionIds.has(o.id));
  if (newlyConsumed.length === 0) {
    return { level, player, nextActiveCollisionIds: activeCollisionIds };
  }

  const ids = new Set(newlyConsumed.map((o) => o.id));
  const nextActive = new Set(activeCollisionIds);
  for (const id of ids) {
    nextActive.add(id);
  }

  const nextLevel = consumeObstacles(level, ids);
  let nextPlayer = player;
  if (!hasRunnerShieldBooster(player)) {
    nextPlayer = applyRunnerPlayerHitUseCase(player, newlyConsumed.length);
  }

  return {
    level: nextLevel,
    player: nextPlayer,
    nextActiveCollisionIds: nextActive,
  };
}

export function handleRunnerSparkCollisions(
  level: RunnerLevel,
  player: RunnerPlayer,
  activeCollisionIds: Set<string>,
): {
  level: RunnerLevel;
  sparksCollected: number;
  nextActiveCollisionIds: Set<string>;
} {
  const hits = detectRunnerSparkCollisions(level, player);
  const newlyConsumed = hits.filter((s) => !activeCollisionIds.has(s.id));
  if (newlyConsumed.length === 0) {
    return { level, sparksCollected: 0, nextActiveCollisionIds: activeCollisionIds };
  }

  const ids = new Set(newlyConsumed.map((s) => s.id));
  const nextActive = new Set(activeCollisionIds);
  for (const id of ids) {
    nextActive.add(id);
  }

  return {
    level: consumeSparks(level, ids),
    sparksCollected: newlyConsumed.length,
    nextActiveCollisionIds: nextActive,
  };
}

export function handleRunnerBoosterCollisions(
  level: RunnerLevel,
  player: RunnerPlayer,
  activeCollisionIds: Set<string>,
): {
  level: RunnerLevel;
  player: RunnerPlayer;
  speedBoostersCollected: number;
  nextActiveCollisionIds: Set<string>;
} {
  const hits = detectRunnerBoosterCollisions(level, player);
  const newlyConsumed = hits.filter((b) => !activeCollisionIds.has(b.id));
  if (newlyConsumed.length === 0) {
    return {
      level,
      player,
      speedBoostersCollected: 0,
      nextActiveCollisionIds: activeCollisionIds,
    };
  }

  const ids = new Set(newlyConsumed.map((b) => b.id));
  const nextActive = new Set(activeCollisionIds);
  for (const id of ids) {
    nextActive.add(id);
  }

  let nextPlayer = player;
  for (const pickup of newlyConsumed) {
    nextPlayer = applyRunnerBoosterPickupUseCase(nextPlayer, pickup.kind);
  }

  return {
    level: consumeBoosters(level, ids),
    player: nextPlayer,
    speedBoostersCollected: newlyConsumed.filter((b) => b.kind === 'speed').length,
    nextActiveCollisionIds: nextActive,
  };
}

/** Одноразовый enter-триггер зоны особого модификатора. */
export function handleRunnerModifierZoneEnter(
  level: RunnerLevel,
  player: RunnerPlayer,
): { level: RunnerLevel; justEntered: boolean } {
  if (!shouldEnterRunnerModifierZone(level, player)) {
    return { level, justEntered: false };
  }
  const zone = level.modifierZone;
  if (zone == null) {
    return { level, justEntered: false };
  }
  let nextLevel: RunnerLevel = {
    ...level,
    modifierZone: { ...zone, entered: true },
  };
  return {
    level: nextLevel,
    justEntered: true,
  };
}
